import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getRiskTableByKiinteisto,
  createRiskTableFromTemplate,
  updateRiskTable,
} from '../../api/api.js';
import { Dropdown } from "react-bootstrap";
import ConfirmModal from "../ConfirmModal.jsx";
import AutoTextarea from "../utils/AutomaticTextArea2.jsx";

export function KiinteistoRiskEditor({ propertyId: propPropertyId }) {
  const location = useLocation();
  const [propertyId, setPropertyId] = useState(propPropertyId || null);
  const [riskidata, setRiskidata] = useState([]);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [tempLabel, setTempLabel] = useState("");
  const [rowToDelete, setRowToDelete] = useState(null);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [tableId, setTableId] = useState(null);
  const [loading, setLoading] = useState(!!propPropertyId);
  const [newSectionName, setNewSectionName] = useState("");

  // Extract propertyId from URL if not passed as prop
  useEffect(() => {
    if (!propPropertyId) {
      const match = location.pathname.match(/taloyhtiokortti\/(\d+)/);
      if (match) {
        setPropertyId(match[1]);
        setLoading(true);
      }
    }
  }, [location.pathname, propPropertyId]);

  // Fetch/init risk table
  useEffect(() => {
    if (!propertyId) return;

const initRiskTable = async () => {
  try {
    const res = await getRiskTableByKiinteisto(propertyId);
    let table;
    if (res.data.length > 0) {
      table = res.data[0];
    } else {
      const createRes = await createRiskTableFromTemplate(propertyId);
      table = createRes.data;

      // ✅ Reset all default risks to empty on fresh creation
      Object.entries(table.data).forEach(([cat, items]) => {
        items.forEach((i) => (i.risk = 0));
      });
    }

    setTableId(table.id);

    const frontendData = Object.entries(table.data).flatMap(([category, items]) =>
      items.map((item, idx) => ({
        uid: `${category}-${idx}-${Date.now()}`,
        category,
        label: item.item,
        risk:
          item.risk === 1
            ? "low"
            : item.risk === 2
            ? "medium"
            : item.risk === 3
            ? "high"
            : "", // ✅ stays empty if risk == 0
        description: item.notes || "",
      }))
    );

    setRiskidata(frontendData);
  } catch (err) {
    console.error("Failed to initialize risk table:", err);
  } finally {
    setLoading(false);
  }
};


    initRiskTable();
  }, [propertyId]);

const saveToBackend = async (dataToSave) => {
  if (!tableId) return;

  const grouped = dataToSave.reduce((acc, item) => {
    (acc[item.category] ||= []).push({
      item: item.label,
      // ✅ Save null or 0 when no risk selected
      risk:
        item.risk === "low"
          ? 1
          : item.risk === "medium"
          ? 2
          : item.risk === "high"
          ? 3
          : 0,
      notes: item.description,
    });
    return acc;
  }, {});

  try {
    await updateRiskTable(tableId, { data: grouped });
  } catch (err) {
    console.error("Failed to save risk table:", err);
  }
};


  const startEditLabel = (item) => {
    setEditingLabelId(item.uid);
    setTempLabel(item.label || "");
  };

  const saveLabel = (uid) => {
    setRiskidata((prev) => {
      const updated = prev.map((i) => (i.uid === uid ? { ...i, label: tempLabel } : i));
      saveToBackend(updated);
      return updated;
    });
    setEditingLabelId(null);
    setTempLabel("");
  };

  const cancelEdit = () => {
    setEditingLabelId(null);
    setTempLabel("");
  };

  const addRow = (category) => {
    setRiskidata((prev) => {
      const updated = [
        ...prev,
        {
          uid: `${category}-${Date.now()}`,
          category,
          label: "Uusi kohde",
          risk: "",
          description: "",
        },
      ];
      saveToBackend(updated);
      return updated;
    });
  };

  const addSection = () => {
    if (!newSectionName.trim()) return;

    setRiskidata((prev) => {
      const updated = [
        ...prev,
        {
          uid: `${newSectionName.trim()}-${Date.now()}`,
          category: newSectionName.trim(),
          label: "Uusi kohde",
          risk: "",
          description: "",
        },
      ];
      saveToBackend(updated);
      return updated;
    });
    setNewSectionName("");
  };

  const updateField = (uid, field, value) => {
    setRiskidata((prev) => {
      const updated = prev.map((i) => (i.uid === uid ? { ...i, [field]: value } : i));
      saveToBackend(updated);
      return updated;
    });
  };

  const deleteRow = (uid) => {
    setRiskidata((prev) => {
      const updated = prev.filter((i) => i.uid !== uid);
      saveToBackend(updated);
      return updated;
    });
    setRowToDelete(null);
  };

  const deleteSection = (category) => {
    setRiskidata((prev) => {
      const updated = prev.filter((i) => i.category !== category);
      saveToBackend(updated);
      return updated;
    });
    setSectionToDelete(null);
  };

  if (!propertyId) {
    return (
      <div className="p-3 border rounded bg-light text-muted">
        Tämä komponentti sisältää kiinteistön riskitaulukon. Lohko tulee näkymään, kun raportti luodaan kiinteistölle.
      </div>
    );
  }

  if (loading) return <div>Loading risk table...</div>;

  const grouped = riskidata.reduce((acc, item) => {
    (acc[item.category] ||= []).push(item);
    return acc;
  }, {});

return (
  <div className="container-lg mt-4">

    {/* New section input */}
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body py-2 px-2 d-flex flex-wrap gap-2 justify-content-center">
        <input
          type="text"
          className="form-control form-control-sm flex-grow-1"
          placeholder="Uuden osion nimi"
          value={newSectionName}
          onChange={(e) => setNewSectionName(e.target.value)}
          style={{ minWidth: "200px" }}
        />
        <button
          type="button"
          className="btn btn-sm btn-success"
          onClick={addSection}
        >
          + Lisää osio
        </button>
      </div>
    </div>

    {/* Sections */}
    {Object.entries(grouped).map(([category, items]) => (
      <div key={category} className="card shadow-sm border-0 mb-3">

        {/* Section Header */}
        <div className="card-header bg-primary bg-opacity-10 border-start border-4 border-primary shadow-sm py-2 px-3">
          <div className="d-flex flex-column flex-md-row justify-content-center justify-content-md-between align-items-center gap-2">
            <h5
              className="fw-bold text-uppercase mb-1 mb-md-0 text-center text-md-start"
              style={{ wordBreak: "break-word" }}
              title={category}
            >
              {category}
            </h5>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => setSectionToDelete(category)}
            >
              Poista osio
            </button>
          </div>
        </div>

        {/* Rows */}
        <div className="card-body py-2 px-2">

          {items.map((item) => {
            const isEditing = editingLabelId === item.uid;
            const riskVariant =
              item.risk === "low"
                ? "success"
                : item.risk === "medium"
                ? "warning"
                : item.risk === "high"
                ? "danger"
                : "secondary";

            return (
              <div key={item.uid} className="row align-items-center mb-2 g-2">

                {/* Label + edit */}
                <div className="col-12 col-md-3 d-flex align-items-center gap-1">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        className="form-control form-control-sm flex-grow-1"
                        value={tempLabel}
                        onChange={(e) => setTempLabel(e.target.value)}
                        autoFocus
                      />
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => saveLabel(item.uid)}
                      >
                        ✓
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={cancelEdit}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <strong
                        className="text-truncate flex-grow-1"
                        title={item.label}
                      >
                        {item.label}
                      </strong>
                      <button
                        className="btn btn-link btn-sm p-0"
                        onClick={() => startEditLabel(item)}
                      >
                        <i className="bi bi-pencil-square text-muted" />
                      </button>
                    </>
                  )}
                </div>

                {/* Risk */}
                <div className="col-12 col-md-3 d-flex justify-content-center">
                  <Dropdown className="w-100">
                    <Dropdown.Toggle
                      size="sm"
                      className="w-100 d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        backgroundColor:
                          item.risk === "low"
                            ? "#04aa00"   // green
                            : item.risk === "medium"
                            ? "#d0c407"   // yellow
                            : item.risk === "high"
                            ? "#ba3b46"   // red
                            : "#ffffff",   // default white if no risk
                        color: item.risk ? "#ffffff" : "#212529", // white text on colored background
                        border: "1px solid rgba(0,0,0,0.15)",
                        fontFamily: "Lato, sans-serif",
                      }}
                    >
                      <span className="me-1">✓</span>
                      {item.risk
                        ? item.risk === "low"
                          ? "Matala"
                          : item.risk === "medium"
                          ? "Keskitason"
                          : "Korkea"
                        : "Valitse riski"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      className="w-100 text-center"
                      style={{
                        backgroundColor: "#f8f9fa", // light grey menu
                        borderRadius: "0.25rem",
                        border: "1px solid rgba(0,0,0,0.15)",
                      }}
                    >
                      <Dropdown.Item
                        onClick={() => updateField(item.uid, "risk", "")}
                        className="lato-textarea text-body"
                      >
                        Tyhjä / Ei valintaa
                      </Dropdown.Item>

                      <Dropdown.Divider />

                      <Dropdown.Item
                        onClick={() => updateField(item.uid, "risk", "low")}
                        className="lato-textarea text-success fw-semibold"
                      >
                        ✓ Matala riski
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => updateField(item.uid, "risk", "medium")}
                        className="lato-textarea text-warning fw-semibold"
                      >
                        ✓ Keskitason riski
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => updateField(item.uid, "risk", "high")}
                        className="lato-textarea text-danger fw-semibold"
                      >
                        ✓ Korkea riski
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>


                  {/* Description */}
                  <div className="col-12 col-md-5">
                    <AutoTextarea
                      value={item.description || ""}
                      onChange={(e) => updateField(item.uid, "description", e.target.value)}
                      placeholder="Selite"
                    />
                  </div>

                {/* Delete */}
                <div className="col-12 col-md-1 d-flex justify-content-center">
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setRowToDelete(item)}
                  >
                    X
                  </button>
                </div>
              </div>
            );
          })}

          <button
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => addRow(category)}
          >
            + Lisää rivi
          </button>

        </div>
      </div>
    ))}

    {/* ✅ Modals */}
    <ConfirmModal
      show={!!rowToDelete}
      onClose={() => setRowToDelete(null)}
      onConfirm={() => deleteRow(rowToDelete.uid)}
      title="Vahvista poisto"
      message={`Haluatko varmasti poistaa rivin "${rowToDelete?.label}"?`}
      confirmText="Poista"
      cancelText="Peruuta"
      confirmVariant="danger"
    />

    <ConfirmModal
      show={!!sectionToDelete}
      onClose={() => setSectionToDelete(null)}
      onConfirm={() => deleteSection(sectionToDelete)}
      title="Vahvista poisto"
      message={`Haluatko varmasti poistaa osion "${sectionToDelete}" ja kaikki sen rivit?`}
      confirmText="Poista osio"
      cancelText="Peruuta"
      confirmVariant="danger"
    />

  </div>
);






}
